import {
  setAudioInputDevice,
  setVideoInputDevice,
  toggleProgramSource,
  deleteMeetingAttendee
} from '@shoflo/studio-redux/actions';
import { stopContentShare, stopVideoInput } from '@studio/chime';
import { dismissAttendee, setProgram } from 'actions/Broadcast';
import { getCameraPresenterScreenShare } from 'actions/Broadcast/utils';
import {
  disableAttendeeScreenShare,
  removeDesiredAttendeeCamera
} from '../attendees';
import deleteBroadcastSource from './deleteBroadcastSource';
import {
  CAMERA_PRESENTER,
  SCREEN_PRESENTER,
  UNASSIGNED,
  VENDOR_AGORA
} from '@studio/constants';

// Ancillary functions
const getSourceValue = source => {
  return source ? source.value : '';
};

const replaceSourceValue = source => {
  let sourceValue = getSourceValue(source);
  if (typeof sourceValue === 'object') {
    return '';
  }
  return sourceValue.replace('#content', '');
};

const screenSharePresenter = (source = {}) => source.type === SCREEN_PRESENTER;
const isCameraPresenter = (source = {}) => source.type === CAMERA_PRESENTER;

/**
 * This disconnects source from the program and releases the media devices.
 * The sources to be disconnected pertain to attendee sources and screen share sources.
 *
 * @param {source} source - the source to be disconnected.
 * @param {meeting} meeting - the current meeting the source is connected to.
 * @param {dispatch} dispatch - the redux store dispatch mechanism.
 */
const disconnectSource = (source, meeting, dispatch) => {
  const sourceAttendeeId = replaceSourceValue(source);
  const isScreenSharePresenter = screenSharePresenter(source);
  if (sourceAttendeeId) {
    const { AttendeeId: attendeeId } = meeting.attendeeResponse.Attendee;
    if (sourceAttendeeId === attendeeId) {
      // We need to make sure we stop the local video tile if the source attendee we just deleted is the director attendee
      if (!isScreenSharePresenter) {
        // Check if source value has content. Shows its a screen share so we skip this
        stopVideoInput(meeting.meetingSession);
        // This needs to be cleared out after
        dispatch(setVideoInputDevice(null));
        // Remove Mic Input
        dispatch(setAudioInputDevice(null));
      }
      if (isScreenSharePresenter) {
        // When its a screen share, on delete we stop the chime screen share session
        stopContentShare(meeting.meetingSession);
      }
    } else {
      // if we have deleted a presenter we need to dispatch a dismissal
      if (!isScreenSharePresenter) {
        dispatch(dismissAttendee(sourceAttendeeId));
      }
    }
  }
};

/**
 * This action creator handles what side effects should be dispatched when a source is about to be deleted
 * @param {string} sourceId
 * @param {object} deleteParams data to help understand subsequent action to perform
 */
export default (sourceId, deleteParams = {}) => {
  return (dispatch, getState) => {
    const { meeting, program, broadcastSources } = getState();
    const [source] = broadcastSources.sources.filter(s => s._id === sourceId);
    const useAgora = deleteParams.vendor === VENDOR_AGORA;

    if (meeting.started) {
      // Check if source is in program and remove before deleting
      if (program) {
        const [layerSource] = program.layers?.filter(
          layer => layer?.source._id === sourceId
        );
        // If source is in Program, update the program.
        if (layerSource) {
          dispatch(toggleProgramSource(source, setProgram));
        }
      }
      if (!useAgora) {
        disconnectSource(source, meeting, dispatch);
      }
    }

    if (isCameraPresenter(source)) {
      // remove attendee camera status
      dispatch(removeDesiredAttendeeCamera(source.value));
      // If camera source has a screen share
      const camScreenShareSource = getCameraPresenterScreenShare(
        source,
        broadcastSources
      );

      if (!!camScreenShareSource && !useAgora) {
        dispatch(disableAttendeeScreenShare(camScreenShareSource));
      }
    }

    dispatch(deleteBroadcastSource(sourceId, deleteParams));

    // delete attendee from chime meeting
    if (source.type === CAMERA_PRESENTER && source.value !== UNASSIGNED) {
      dispatch(deleteMeetingAttendee(source.value));
    }
  };
};
